/**
 * Layout Algorithm
 * District placement (radial zones) and building scatter within district polygons.
 */

import type {
  Point,
  District,
  Building,
  DistrictType,
  BuildingCategory,
  SettlementTier,
  RotatedRect,
  SettlementConfig,
} from './types';
import { SeededRandom } from './random';
import {
  point,
  fromPolar,
  irregularPolygonWithRadii,
  rectsOverlap,
  rectInsidePolygon,
  boundingBox,
} from './geometry';
import {
  TIER_TEMPLATES,
  DISTRICT_TEMPLATES,
  BUILDING_TEMPLATES,
  BUILDING_FOOTPRINTS,
} from './templates';

// ============================================================================
// ZONE RADII (settlement-local units, ~1 unit = 5m)
// ============================================================================

const ZONE_RADII: Record<SettlementTier, number[]> = {
  hamlet:  [0, 30, 60],
  village: [0, 40, 80, 120],
  town:    [0, 50, 100, 150, 200],
  city:    [0, 60, 120, 180, 240],
};

const DISTRICT_RADIUS: Record<SettlementTier, number> = {
  hamlet: 25,
  village: 30,
  town: 35,
  city: 40,
};

// ============================================================================
// DISTRICT PLACEMENT
// ============================================================================

export function placeDistricts(config: SettlementConfig, rng: SeededRandom): District[] {
  const tier = TIER_TEMPLATES[config.tier];
  const zoneRadii = ZONE_RADII[config.tier];
  const districtRadius = DISTRICT_RADIUS[config.tier];

  // Determine how many districts
  const districtCount = rng.int(tier.districtCount[0], tier.districtCount[1]);

  // Always have center district
  const districtTypes: DistrictType[] = ['center'];

  // Fill from required buildings' preferred districts, then from district pool
  const pool = [...tier.districtPool].filter(d => d !== 'center');

  // Add docks if coastal/river
  if ((config.terrain.hasRiver || config.terrain.isCoastal) && pool.includes('docks')) {
    if (!districtTypes.includes('docks')) {
      districtTypes.push('docks');
    }
  }

  // Fill remaining slots
  rng.shuffle(pool);
  for (const dt of pool) {
    if (districtTypes.length >= districtCount) break;
    if (!districtTypes.includes(dt)) {
      districtTypes.push(dt);
    }
  }

  // If still need more, add duplicates of residential/farmland
  const fillers: DistrictType[] = ['residential', 'farmland', 'residential'];
  let fillerIdx = 0;
  while (districtTypes.length < districtCount && fillerIdx < fillers.length) {
    districtTypes.push(fillers[fillerIdx++]);
  }

  // Place districts at zone-appropriate positions
  const districts: District[] = [];
  const usedAngles: number[] = [];

  for (let i = 0; i < districtTypes.length; i++) {
    const type = districtTypes[i];
    const template = DISTRICT_TEMPLATES[type];
    let center: Point;

    if (i === 0) {
      // Center district at origin
      center = point(0, 0);
    } else {
      // Place in appropriate zone with angular jitter
      const zone = Math.min(template.zone, zoneRadii.length - 2);
      const minR = zoneRadii[zone] || zoneRadii[1];
      const maxR = zoneRadii[zone + 1] || zoneRadii[zoneRadii.length - 1];
      const radius = rng.range(minR + 5, maxR - 5);

      // Pick angle avoiding too-close neighbors
      let angle = rng.angle();
      let attempts = 0;
      while (attempts < 20) {
        const tooClose = usedAngles.some(a => {
          const diff = Math.abs(angle - a);
          const normalizedDiff = Math.min(diff, Math.PI * 2 - diff);
          return normalizedDiff < (Math.PI * 2) / (districtCount + 2);
        });
        if (!tooClose) break;
        angle = rng.angle();
        attempts++;
      }
      usedAngles.push(angle);
      center = fromPolar(angle, radius);
    }

    // Generate irregular polygon boundary
    const vertexCount = rng.int(8, 12);
    const radii: number[] = [];
    for (let v = 0; v < vertexCount; v++) {
      radii.push(districtRadius * rng.range(0.7, 1.3));
    }
    const startAngle = rng.angle();
    const boundary = irregularPolygonWithRadii(center, radii, startAngle);

    districts.push({
      id: `district_${i}`,
      type,
      center,
      boundary,
      placeholderName: rng.pick(template.placeholderNames),
      flavoredName: null,
      tags: [],
      atmosphere: rng.pick(template.atmospheres),
    });
  }

  return districts;
}

// ============================================================================
// BUILDING PLACEMENT
// ============================================================================

export function placeBuildings(
  config: SettlementConfig,
  districts: District[],
  rng: SeededRandom,
): Building[] {
  const tier = TIER_TEMPLATES[config.tier];
  const targetCount = rng.int(tier.buildingCount[0], tier.buildingCount[1]);

  // Build the required building list
  const requiredCategories: BuildingCategory[] = [
    ...tier.requiredBuildings,
    ...(config.requiredBuildings ?? []),
  ];

  // Track placed buildings for overlap detection
  const placedRects: RotatedRect[] = [];
  const buildings: Building[] = [];
  let buildingIdx = 0;

  // Helper: find best district for a building category
  const findDistrict = (category: BuildingCategory): District => {
    const template = BUILDING_TEMPLATES[category];
    // Try preferred districts first
    for (const prefType of template.preferredDistricts) {
      const match = districts.find(d => d.type === prefType);
      if (match) return match;
    }
    // Fallback to random district
    return rng.pick(districts);
  };

  // Helper: try to place a building in a district
  const tryPlace = (category: BuildingCategory, district: District): Building | null => {
    const template = BUILDING_TEMPLATES[category];
    const footprintDims = BUILDING_FOOTPRINTS[template.size];
    const bbox = boundingBox(district.boundary);

    // Try up to 30 random positions
    for (let attempt = 0; attempt < 30; attempt++) {
      const pos: Point = {
        x: rng.range(bbox.minX + footprintDims.width, bbox.maxX - footprintDims.width),
        y: rng.range(bbox.minY + footprintDims.height, bbox.maxY - footprintDims.height),
      };

      // For first building in district, bias toward center
      if (attempt === 0 && buildings.filter(b => b.districtId === district.id).length === 0) {
        pos.x = district.center.x + rng.range(-5, 5);
        pos.y = district.center.y + rng.range(-5, 5);
      }

      const rotation = rng.range(-0.3, 0.3); // slight rotation for organic feel
      const rect: RotatedRect = {
        center: pos,
        width: footprintDims.width,
        height: footprintDims.height,
        rotation,
      };

      // Check: inside district boundary
      if (!rectInsidePolygon(rect, district.boundary)) continue;

      // Check: no overlap with existing buildings (2-unit padding for paths)
      const overlaps = placedRects.some(existing => rectsOverlap(rect, existing, 2));
      if (overlaps) continue;

      // Place it!
      placedRects.push(rect);
      const npcSlots = template.npcRoles.map(role => ({
        role,
        placeholderName: `The ${role.charAt(0).toUpperCase() + role.slice(1)}`,
        flavoredName: null,
      }));

      const building: Building = {
        id: `building_${buildingIdx++}`,
        category,
        districtId: district.id,
        footprint: rect,
        size: template.size,
        placeholderName: rng.pick(template.placeholderNames),
        flavoredName: null,
        placeholderDescription: `A ${template.size} ${category.replace(/_/g, ' ')}`,
        flavoredDescription: null,
        tags: [...template.tags],
        services: [...template.services],
        npcSlots,
        visualStyle: { ...template.visualStyle },
      };

      return building;
    }

    return null; // couldn't place after 30 attempts
  };

  // Phase 1: Place required buildings
  for (const category of requiredCategories) {
    const district = findDistrict(category);
    const building = tryPlace(category, district);
    if (building) {
      buildings.push(building);
    }
  }

  // Phase 2: Fill to target count with appropriate buildings
  const fillCategories: BuildingCategory[] = [
    'residence', 'residence', 'residence', 'residence',
    'tavern', 'general_store', 'blacksmith', 'stable',
    'shrine', 'well', 'warehouse', 'residence',
    'apothecary', 'monument', 'tannery', 'mill',
    'farm', 'residence', 'residence',
  ];

  let fillIdx = 0;
  while (buildings.length < targetCount && fillIdx < fillCategories.length * 3) {
    const category = fillCategories[fillIdx % fillCategories.length];
    const district = findDistrict(category);
    const building = tryPlace(category, district);
    if (building) {
      buildings.push(building);
    }
    fillIdx++;
  }

  return buildings;
}

// ============================================================================
// COMPUTE SETTLEMENT BOUNDS
// ============================================================================

export function computeBounds(
  districts: District[],
  extraPoints: Point[] = [],
): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

  for (const d of districts) {
    for (const p of d.boundary) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
  }

  for (const p of extraPoints) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }

  // Add padding
  const pad = 20;
  return { minX: minX - pad, minY: minY - pad, maxX: maxX + pad, maxY: maxY + pad };
}
